import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Clock, Shield, MessageSquare } from 'lucide-react';
import SupportForm from '../components/SupportForm';
import { ContactPageSEO } from '../components/SEO';

export const ContactPage = () => {
    return (
        <>
            <ContactPageSEO />

            <div className="section-gap" data-testid="contact-page">
                <div className="container-main max-w-4xl">
                    {/* Header */}
                    <div className="mb-8">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            Get in touch
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-primary leading-tight mt-4">
                            Contact Zarolla Support
                        </h1>
                        <p className="text-muted-foreground mt-4 max-w-2xl">
                            Questions about courses, video access, your account, or anything else? Our team is here to help and typically responds within one business day.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                        {/* Main Form - Takes 3 columns */}
                        <div className="lg:col-span-3">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-serif text-2xl flex items-center gap-2">
                                        <MessageSquare className="h-6 w-6 text-accent" />
                                        Send a Message
                                    </CardTitle>
                                    <CardDescription>
                                        Have a question about your account, courses, or technical issues? 
                                        We're here to help.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <SupportForm />
                                </CardContent>
                            </Card>
                        </div>

                        {/* Info Sidebar - Takes 2 columns */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Response Time */}
                            <Card className="bg-secondary/30">
                                <CardContent className="pt-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                                            <Clock className="h-5 w-5 text-accent" />
                                        </div>
                                        <div>
                                            <h2 className="font-medium mb-1">Quick Response</h2>
                                            <p className="text-sm text-muted-foreground">
                                                Support requests are typically answered within 1 business day.
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Security Note */}
                            <Card className="bg-secondary/30">
                                <CardContent className="pt-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                            <Shield className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <h2 className="font-medium mb-1">Secure Communication</h2>
                                            <p className="text-sm text-muted-foreground">
                                                Your message is sent directly to our support team through a secure channel.
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* FAQ Hint */}
                            <Card className="border-accent/30">
                                <CardContent className="pt-6">
                                    <h2 className="font-medium mb-2">Common Questions</h2>
                                    <ul className="text-sm text-muted-foreground space-y-2">
                                        <li>• How do I access my purchased videos?</li>
                                        <li>• What payment methods do you accept?</li>
                                        <li>• Can I download videos for offline viewing?</li>
                                        <li>• How do I reset my password?</li>
                                    </ul>
                                    <p className="text-sm text-muted-foreground mt-4">
                                        Visit our <Link to="/about" className="text-accent hover:underline">About page</Link> for more information about our courses and instructors.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContactPage;
